/**
 * This is a temporary solution to show the human supervisor avatar in the assignee field for Team EU 25.
 * To be cleaned up in https://jplat.atlassian.net/browse/CRCS-3113 once we settle and move to a long term solution.
 */
import { useState, useEffect, useRef } from 'react';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { isDataExtra } from '@atlassian/jira-platform-services-user-current/src/types.tsx';
import { getClient } from '@atlassian/jira-issue-reliability-fetch/src/issue-view-gira-client.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AggUser } from '@atlassian/jira-issue-user-picker-edit-view/src/common/types.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import type { assignee_issueFieldAssigneeInlineEditFull_AssigneeInlineEditViewWithIsEditable_fragmentRef$data } from '@atlassian/jira-relay/src/__generated__/assignee_issueFieldAssigneeInlineEditFull_AssigneeInlineEditViewWithIsEditable_fragmentRef.graphql';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';

const IssueHistoryQuery = `
	query IssueHistoryQuery($issueKey: String!, $startAt: Long, $maxResults: Int, $orderBy: String) {
		viewIssue(issueKey: $issueKey) {
			history(orderBy: $orderBy, startAt: $startAt, maxResults: $maxResults) {
				isLast
				totalCount
				startIndex
				nodes {
					fieldId
					actor {
						avatarUrls {
							large
						}
						displayName
						accountId
					}
					to {
						... on AssigneeFieldValue {
							displayValue
							value
							avatarUrl
						}
					}
				}
			}
		}
	}
`;

type QueryResponse = {
	data?: {
		viewIssue?: {
			history?: {
				isLast: boolean;
				totalCount: number;
				startIndex: number;
				nodes?: Array<{
					fieldId: string;
					actor: {
						avatarUrls: {
							large: string;
						};
						displayName: string;
						accountId: string;
					};
					to?: {
						displayValue: string;
						value: string;
						avatarUrl: string;
					};
				}>;
			};
		};
	};
	errors?: Array<{
		message: string;
		locations?: Array<{
			line: number;
			column: number;
		}>;
		path?: string[];
	}>;
};

const useSupervisor = (
	user: assignee_issueFieldAssigneeInlineEditFull_AssigneeInlineEditViewWithIsEditable_fragmentRef$data['user'],
	updatedValue: AggUser | null,
) => {
	const [humanSupervisor, setHumanSupervisor] = useState<{
		accountId: string;
		displayName: string;
		avatarUrls: { large: string };
	} | null>(null);
	const issueKey = useIssueKey();
	const prevUpdatedValue = usePrevious(updatedValue);
	const hasInitialDataLoadedRef = useRef(false);

	const {
		data: { user: currentUser },
	} = useCurrentUser();

	// For agent assignee, load human supervisor from issue history, when issue or user changes
	useEffect(() => {
		if (!issueKey) {
			return;
		}
		if (user?.appType !== 'agent') {
			hasInitialDataLoadedRef.current = true;
			setHumanSupervisor(null);
			return;
		}

		const history: Promise<QueryResponse> = getClient(
			'fields/platform/user/assignee/inline-edit-full/src/ui/assignee/index',
		).query({
			query: IssueHistoryQuery,
			variables: {
				issueKey,
				startAt: 0,
				maxResults: 5, // Get enough to find assignee changes
				orderBy: '-created',
			},
		});

		history.then((result) => {
			const issueHistoryNodes = result?.data?.viewIssue?.history?.nodes;

			const mostRecentAgentAssignmentRecord = issueHistoryNodes?.find(
				(node) => node.fieldId === 'assignee' && node.to?.displayValue === user?.name,
			);

			if (mostRecentAgentAssignmentRecord) {
				setHumanSupervisor({
					...mostRecentAgentAssignmentRecord.actor,
					displayName:
						currentUser.accountId === mostRecentAgentAssignmentRecord.actor.accountId
							? 'You'
							: mostRecentAgentAssignmentRecord.actor.displayName,
				});
			}
			hasInitialDataLoadedRef.current = true;
		});
		// TODO: error handling
	}, [issueKey, user?.accountId, user?.name, user?.appType, currentUser.accountId]); // accountId here to protect again identical agent names

	// Optimistic update: set supervisor to current user when assignee **changed** from client side
	useEffect(() => {
		// skip when assignee didn't really change, or before initial data is loaded
		if (!hasInitialDataLoadedRef.current || prevUpdatedValue?.id === updatedValue?.id) {
			return;
		}
		if (updatedValue?.appType !== 'agent') {
			setHumanSupervisor(null);
			return;
		}

		setHumanSupervisor({
			accountId: currentUser.accountId ?? '',
			displayName: 'You',
			avatarUrls: { large: isDataExtra(currentUser) ? currentUser.avatarUrls['48x48'] : '' },
		});
	}, [updatedValue, prevUpdatedValue, currentUser]);

	return {
		humanSupervisor,
	};
};

export const useSupervisorWithFg = functionWithCondition(
	() => fg('jira_ai_show_human_supervisor_avatar'),
	useSupervisor,
	() => ({ humanSupervisor: null }),
);
